import cn from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  approveAiContent,
  fetchAiDrafts,
  rejectAiContent,
} from "../../../app/aiContent/aiContent.slice";
import Card from "../../../components/Card";
import Icon from "../../../components/Icon";
import {
  getAiItemId,
  getAiItemSubtitle,
  getAiItemTitle,
  isAiItemPremium,
} from "../../AiDrafts/helpers";
import styles from "./AiDrafts.module.sass";

const PREVIEW_TAKE = 5;

const deckEmoji = (title = "") => {
  const s = String(title).toLowerCase();
  if (/negocio|business|b2b|reunion|reunião/.test(s)) return "💼";
  if (/negociac|acuerdo|formal/.test(s)) return "🤝";
  if (/medic|salud|health/.test(s)) return "🩺";
  if (/emoc|feel|mood/.test(s)) return "🙂";
  if (/financ|econom|money/.test(s)) return "💹";
  if (/travel|viaj|world|word/.test(s)) return "🌍";
  if (/mcp|smoke|test/.test(s)) return "🧪";
  if (/hsk/.test(s)) return "🀄";
  return "📘";
};

const gradients = [
  ["#FFBC99", "#FF8A65"],
  ["#CABDFF", "#8E59FF"],
  ["#B1E5FC", "#2A85FF"],
  ["#B5E4CA", "#83BF6E"],
  ["#FFD88D", "#FFB020"],
];
const gradFor = (str = "") => {
  let h = 0;
  for (const c of str) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
  const [a, b] = gradients[Math.abs(h) % gradients.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
};

// The card "title" (from helpers) is the character; the thumbnail shows the hanzi
// scaled to fit — big for 1-2 chars, smaller for 4-5.
const getAiItemHanzi = (item) => (Array.isArray(item?.meaning) ? item?.meaning[0] : item?.meaning)?.character || item?.character || getAiItemTitle(item);
const hanziSize = (str = "") => {
  const n = String(str).replace(/\s/g, "").length;
  if (n <= 1) return "28px";
  if (n === 2) return "24px";
  if (n === 3) return "18px";
  if (n === 4) return "15px";
  return "13px";
};

/**
 * Home section: "Contenido IA (borradores)" — AI-generated decks & cards in
 * draft status, pending manual review (approve / reject).
 */
const AiDrafts = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { aiContent } = useSelector((state) => state);

  const [activeType, setActiveType] = useState("deck");
  const userPickedType = useRef(false);
  const pickType = (type) => {
    userPickedType.current = true;
    setActiveType(type);
  };

  useEffect(() => {
    dispatch(fetchAiDrafts({ type: "deck", page: 0, take: PREVIEW_TAKE }));
    dispatch(fetchAiDrafts({ type: "card", page: 0, take: PREVIEW_TAKE }));
    // Cards cuya glosa coincide con la de una card publicada: van aparte.
    dispatch(fetchAiDrafts({ type: "card", page: 0, take: PREVIEW_TAKE, collisions: "only" }));
  }, [dispatch]);

  const bucket =
    activeType === "collision"
      ? aiContent?.collisions
      : activeType === "card"
      ? aiContent?.cards
      : aiContent?.decks;
  const items = bucket?.items || [];

  const decksCount = aiContent?.decks?.pagination?.total ?? aiContent?.decks?.items?.length ?? 0;
  const cardsCount = aiContent?.cards?.pagination?.total ?? aiContent?.cards?.items?.length ?? 0;
  const collisionsCount =
    aiContent?.collisions?.pagination?.total ?? aiContent?.collisions?.items?.length ?? 0;
  const totalCount = decksCount + cardsCount + collisionsCount;

  // Al cargar, salta a la pestaña que tenga pendientes (si el usuario no eligió ya).
  useEffect(() => {
    if (!userPickedType.current && decksCount === 0 && cardsCount > 0) {
      setActiveType("card");
    }
  }, [decksCount, cardsCount]);

  const refresh = () => {
    dispatch(
      fetchAiDrafts({
        type: activeType === "collision" ? "card" : activeType,
        page: 0,
        take: PREVIEW_TAKE,
        collisions: activeType === "collision" ? "only" : undefined,
      })
    );
  };

  const handleApprove = async (item) => {
    const result = await dispatch(approveAiContent({ type: activeType === "collision" ? "card" : activeType, id: getAiItemId(item) }));
    if (approveAiContent.fulfilled.match(result)) {
      toast.success(t("aiDrafts.approvedToast"));
      refresh();
    } else {
      toast.error(t("toast.errorOccurred"));
    }
  };

  const handleReject = async (item) => {
    if (!window.confirm(t("aiDrafts.confirmReject", { title: getAiItemTitle(item) }))) return;
    const result = await dispatch(rejectAiContent({ type: activeType === "collision" ? "card" : activeType, id: getAiItemId(item) }));
    if (rejectAiContent.fulfilled.match(result)) {
      toast.success(t("aiDrafts.rejectedToast"));
      refresh();
    } else {
      toast.error(t("toast.errorOccurred"));
    }
  };

  return (
    <Card
      className={cn(styles.card, className)}
      title={t("aiDrafts.title")}
      classTitle="title-purple"
      head={
        <div className={styles.headRow}>
          {totalCount > 0 && (
            <div className={styles.counter}>
              {totalCount} {t("aiDrafts.pending", "pendientes")}
            </div>
          )}
          <Link
            className={styles.reviewButton}
            to={
              activeType === "collision"
                ? "/ai-content/review?type=card&collisions=only"
                : `/ai-content/review?type=${activeType}`
            }
          >
            {t("aiDrafts.reviewOneByOne")} →
          </Link>
          <Link className={cn("button-stroke", "button-small", styles.viewAll)} to="/ai-content/drafts">
            {t("aiDrafts.viewAll")}
          </Link>
        </div>
      }
    >
      <div className={styles.nav}>
        <button
          type="button"
          className={cn(styles.link, { [styles.active]: activeType === "deck" })}
          onClick={() => pickType("deck")}
        >
          {t("aiDrafts.decks")}
          <span className={styles.navCounter}>{decksCount}</span>
        </button>
        <button
          type="button"
          className={cn(styles.link, { [styles.active]: activeType === "card" })}
          onClick={() => pickType("card")}
        >
          {t("aiDrafts.cards")}
          <span className={styles.navCounter}>{cardsCount}</span>
        </button>
        <button
          type="button"
          className={cn(styles.link, { [styles.active]: activeType === "collision" })}
          onClick={() => pickType("collision")}
        >
          {t("aiDrafts.collisions", "Glosa repetida")}
          <span className={styles.navCounter}>{collisionsCount}</span>
        </button>
      </div>

      {bucket?.isLoading && items.length === 0 ? (
        <div className={styles.empty}>{t("common.loading")}...</div>
      ) : items.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>✅</div>
          <div>{t("aiDrafts.empty")}</div>
        </div>
      ) : (
        <div className={styles.list}>
          {items.map((item, index) => {
            const title = getAiItemTitle(item);
            const isCard = activeType === "card" || activeType === "collision";
            const editUrl = isCard
              ? `/card/edit/${item?._id ?? getAiItemId(item)}`
              : `/deck/edit/${item?._id ?? getAiItemId(item)}`;
            return (
              <div className={styles.item} key={getAiItemId(item) ?? index}>
                <Link to={editUrl} className={styles.linkArea} title={t("aiDrafts.openDetail", "Abrir detalle")}>
                  <div
                    className={cn(styles.thumb, { [styles.thumbCard]: isCard })}
                    style={isCard ? undefined : { background: gradFor(title) }}
                  >
                    {isCard ? (
                      <span
                        className={styles.hanzi}
                        style={{ fontSize: hanziSize(getAiItemTitle(item)) }}
                      >
                        {getAiItemHanzi(item)}
                      </span>
                    ) : (
                      <span>{deckEmoji(title)}</span>
                    )}
                  </div>
                  <div className={styles.details}>
                  <div className={styles.title}>
                    {isCard ? getAiItemSubtitle(item, activeType) || title : title}
                  </div>
                  <div className={styles.line}>
                    <span className={cn(styles.badge, styles.badgeDraft)}>{t("aiDrafts.draft")}</span>
                    <span className={cn(styles.badge, styles.badgeAi)}>
                      <Icon name="lightning" size="12" /> {t("aiDrafts.ai")}
                    </span>
                    {isAiItemPremium(item) && (
                      <span className={cn(styles.badge, styles.badgePremium)}>
                        {t("common.premium")}
                        {item?.premium?.cost ? ` · ${item.premium.cost}€` : ""}
                      </span>
                    )}
                    {!isCard && getAiItemSubtitle(item, activeType) && (
                      <span className={styles.subtitle}>{getAiItemSubtitle(item, activeType)}</span>
                    )}
                  </div>
                  </div>
                </Link>
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.approve}
                    disabled={aiContent?.isActing}
                    onClick={() => handleApprove(item)}
                  >
                    <Icon name="check" size="16" />
                    {t("aiDrafts.approve")}
                  </button>
                  <button
                    type="button"
                    className={styles.reject}
                    disabled={aiContent?.isActing}
                    onClick={() => handleReject(item)}
                  >
                    <Icon name="close" size="16" />
                    {t("aiDrafts.reject")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default AiDrafts;
